import React, { useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const DestinationScroll = ({ onSelectCountry }) => {
    const sectionRef = useRef(null);
    const cardsRef = useRef([]);
    const [activeIndex, setActiveIndex] = useState(0);

    const destinations = [
        {
            flag: '🇦🇺',
            name: 'Australia',
            desc: 'Top universities, post-study work opportunities & PR pathways',
            country: 'australia',
            image: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=800&q=80',
            highlights: ['World-class education', 'Post-study work visa', 'PR pathways', 'Multicultural society'],
        },
        {
            flag: '🇬🇧',
            name: 'United Kingdom',
            desc: 'Globally recognized degrees with shorter course duration',
            country: 'uk',
            image: 'https://images.unsplash.com/photo-1513635269975-59663e0ac1ad?w=800&q=80',
            highlights: ['Historic universities', 'Shorter course duration', 'Global recognition', 'Rich culture'],
        },
        {
            flag: '🇺🇸',
            name: 'United States',
            desc: 'World-class education with excellent career opportunities',
            country: 'usa',
            image: 'https://images.unsplash.com/photo-1501594907352-04cda38ebc29?w=800&q=80',
            highlights: ['Top-ranked universities', 'Research opportunities', 'Career prospects', 'Diverse programs'],
        },
        {
            flag: '🇨🇦',
            name: 'Canada',
            desc: 'Affordable education with high visa success rate',
            country: 'canada',
            image: 'https://images.unsplash.com/photo-1519677100203-a0e668c92439?w=800&q=80',
            highlights: ['Affordable tuition', 'High visa success', 'PR opportunities', 'Safe environment'],
        },
        {
            flag: 'NZ',
            name: 'New Zealand',
            desc: 'Trusted consultants with strong visa success and full support',
            country: 'new-zealand',
            image: 'https://images.unsplash.com/photo-1469474968028-56623f02e42e?auto=format&fit=crop&w=1200&q=90',
            highlights: ['Globally recognized universities', 'Safe environment', 'Post-study work', 'PR pathways'],
        },
        {
            flag: '🇪🇺',
            name: 'Europe',
            desc: 'Affordable education in top countries',
            country: 'europe',
            image: 'https://images.unsplash.com/photo-1467269204594-9661b134dd2b?w=800&q=80',
            highlights: ['Low tuition fees', 'Rich history', 'Travel opportunities', 'Quality education'],
        },
    ];

    useEffect(() => {
        const ctx = gsap.context(() => {
            cardsRef.current.forEach((card, index) => {
                gsap.fromTo(
                    card,
                    {
                        opacity: 0,
                        scale: 0.8,
                        y: 100,
                    },
                    {
                        opacity: 1,
                        scale: 1,
                        y: 0,
                        duration: 1,
                        ease: 'power3.out',
                        scrollTrigger: {
                            trigger: card,
                            start: 'top 80%',
                            end: 'top 40%',
                            scrub: 1,
                            onEnter: () => setActiveIndex(index),
                            onEnterBack: () => setActiveIndex(index),
                        },
                    }
                );
            });
        }, sectionRef);

        return () => ctx.revert();
    }, []);

    return (
        <section
            ref={sectionRef}
            id="destinations"
            className="py-20 bg-white"
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Section Header */}
                <div className="text-center mb-16">
                    <div className="flex items-center justify-center gap-3 mb-4">
                        <i className="fas fa-globe text-primary-red text-2xl"></i>
                        <h2 className="section-title">Choose Your Dream Destination</h2>
                    </div>
                    <p className="section-subtitle">
                        Explore top study abroad destinations with expert guidance
                    </p>
                </div>

                {/* Destination Cards */}
                <div className="space-y-32">
                    {destinations.map((dest, index) => (
                        <div
                            key={index}
                            ref={(el) => (cardsRef.current[index] = el)}
                            className={`destination-scroll-card flex flex-col ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'
                                } items-center gap-12`}
                        >
                            {/* Image */}
                            <div className="flex-1 relative">
                                <div className="relative overflow-hidden rounded-2xl shadow-2xl">
                                    <img
                                        src={dest.image}
                                        alt={dest.name}
                                        className="w-full h-80 lg:h-96 object-cover transition-transform duration-700 hover:scale-110"
                                        loading="lazy"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                                    <div className="absolute bottom-6 left-6 text-white">
                                        <span className="text-5xl block mb-2">{dest.flag}</span>
                                        <h3 className="text-2xl font-bold">{dest.name}</h3>
                                    </div>
                                </div>
                                {/* Decorative element */}
                                <div className="absolute -top-4 -right-4 w-24 h-24 bg-primary-red/10 rounded-full -z-10"></div>
                                <div className="absolute -bottom-4 -left-4 w-16 h-16 bg-primary-red/10 rounded-full -z-10"></div>
                            </div>

                            {/* Content */}
                            <div className="flex-1">
                                <div className="inline-flex items-center gap-2 bg-primary-red/10 text-primary-red px-4 py-2 rounded-full text-sm font-semibold mb-4">
                                    <i className="fas fa-star"></i>
                                    <span>Popular Destination</span>
                                </div>
                                <h3 className="text-3xl md:text-4xl font-bold text-black mb-4">
                                    {dest.name}
                                </h3>
                                <p className="text-gray text-lg mb-6 leading-relaxed">
                                    {dest.desc}
                                </p>

                                {/* Highlights */}
                                <div className="grid grid-cols-2 gap-4 mb-8">
                                    {dest.highlights.map((highlight, hIndex) => (
                                        <div key={hIndex} className="flex items-center gap-2">
                                            <div className="w-2 h-2 bg-primary-red rounded-full"></div>
                                            <span className="text-gray">{highlight}</span>
                                        </div>
                                    ))}
                                </div>

                                {/* CTA */}
                                <button
                                    onClick={() => onSelectCountry && onSelectCountry(dest.country)}
                                    className="btn-primary inline-flex items-center gap-2"
                                >
                                    <i className="fas fa-arrow-right"></i>
                                    Explore {dest.name}
                                </button>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Progress Indicator */}
                <div className="flex justify-center gap-2 mt-16">
                    {destinations.map((_, index) => (
                        <div
                            key={index}
                            className={`w-3 h-3 rounded-full transition-all duration-300 ${index === activeIndex
                                    ? 'bg-primary-red scale-125'
                                    : 'bg-gray-300'
                                }`}
                        ></div>
                    ))}
                </div>
            </div>
        </section>
    );
};

export default DestinationScroll;

